import dayjs from 'dayjs';
import { isNil } from 'lodash-es';

// The locale used to format dates/times app-wide. Defaults to en-US and is set
// from the logged-in user's culture once the session loads (see app/index.tsx).
// Kept as a module-level value (not a hook) because formatDateString is called
// from ~34 non-hook call sites (table column defs, RenderRow callbacks, etc.).
let activeLocale = 'en-US';

// Cached Intl formatters per locale. Date and time are formatted separately and
// joined with a space, which keeps the existing space-separated "date time" look
// and avoids the locale-specific connector (e.g. the comma Intl inserts between
// date and time for many locales).
const cache = new Map<string, { date: Intl.DateTimeFormat; time: Intl.DateTimeFormat }>();

function formatters(locale: string) {
  let f = cache.get(locale);
  if (!f) {
    const make = (opts: Intl.DateTimeFormatOptions) => {
      try {
        return new Intl.DateTimeFormat(locale, opts);
      } catch {
        // Unknown/invalid culture name → fall back to en-US.
        return new Intl.DateTimeFormat('en-US', opts);
      }
    };
    f = {
      // hour: 'numeric' lets the locale decide 12h (en-US → 1:21 PM) vs 24h (da-DK → 13.21).
      date: make({ year: 'numeric', month: 'numeric', day: 'numeric' }),
      time: make({ hour: 'numeric', minute: '2-digit' }),
    };
    cache.set(locale, f);
  }
  return f;
}

/**
 * Sets the locale used by formatDateString. Call once after the user session
 * loads. Clears the formatter cache so a changed locale takes effect.
 */
export function setActiveLocale(locale?: string | null) {
  activeLocale = locale && locale.trim() ? locale.trim() : 'en-US';
  cache.clear();
}

export function formatDateString(date?: null | number | string | Date | dayjs.Dayjs) {
  if (isNil(date) || date === '') return ''; // empty date
  const d = dayjs(date);
  if (!d.isValid()) return ''; // guard: Intl.DateTimeFormat throws on invalid dates
  const f = formatters(activeLocale);
  const native = d.toDate();
  return `${f.date.format(native)} ${f.time.format(native)}`;
}

// Date-only, locale-aware (no time component).
export function formatDateOnly(date?: null | number | string | Date | dayjs.Dayjs) {
  if (isNil(date) || date === '') return '';
  const d = dayjs(date);
  if (!d.isValid()) return '';
  return formatters(activeLocale).date.format(d.toDate());
}

// Time-only, locale-aware (12h vs 24h decided by the locale).
export function formatTimeOnly(date?: null | number | string | Date | dayjs.Dayjs) {
  if (isNil(date) || date === '') return '';
  const d = dayjs(date);
  if (!d.isValid()) return '';
  return formatters(activeLocale).time.format(d.toDate());
}
